import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, TrendingUp, Package, Pencil, X, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TherapistPerformance = ({ user, onLogout }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/services/my-services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (service) => {
    // Check if service is within 24 hours
    const serviceDate = new Date(service.created_at);
    const hoursSince = (new Date() - serviceDate) / (1000 * 60 * 60);
    
    if (hoursSince > 24) {
      toast.error('Services can only be edited within 24 hours of creation');
      return;
    }
    
    setEditingService(service);
    setEditForm({
      customer_name: service.customer_name || '',
      customer_phone: service.customer_phone || '',
      therapy_type: service.therapy_type || '',
      base_price: service.base_price?.toString() || '',
      discount_amount: service.discount_amount?.toString() || '0',
      discount_reason: service.discount_reason || '',
      notes: service.notes || ''
    });
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/services/${editingService.service_id}`, {
        ...editForm,
        base_price: parseFloat(editForm.base_price),
        discount_amount: parseFloat(editForm.discount_amount) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Service updated successfully');
      setEditingService(null);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update service');
    }
  };

  const totalServices = services.length;
  const totalRevenue = services.reduce((sum, s) => sum + s.base_price, 0);

  // Check if a service is editable (within 24 hours)
  const isEditable = (service) => {
    const serviceDate = new Date(service.created_at);
    const hoursSince = (new Date() - serviceDate) / (1000 * 60 * 60);
    return hoursSince <= 24;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-white/80 backdrop-blur-sm border-b border-border/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/therapist">
              <Button variant="outline" size="sm" data-testid="back-button">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </Link>
            <h1 className="text-2xl font-serif text-foreground">My Performance</h1>
          </div>
          <Button variant="outline" size="sm" onClick={onLogout}>
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground">Total Services</p>
            </div>
            <p className="text-3xl font-medium text-foreground">{totalServices}</p>
          </div>

          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-6 h-6 text-accent" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground">Total Revenue (Base)</p>
            </div>
            <p className="text-3xl font-medium text-foreground">₹{totalRevenue.toLocaleString()}</p>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-serif text-foreground mb-4">Service History</h3>
          <p className="text-xs text-muted-foreground mb-4">Services can be edited within 24 hours of creation</p>
          <div className="space-y-3">
            {loading ? (
              <p className="text-center text-muted-foreground py-8">Loading...</p>
            ) : services.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No services recorded yet</p>
            ) : (
              services.map((service, index) => (
                <div key={index} className="p-4 bg-muted/30 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-foreground">{service.customer_name}</p>
                      <p className="text-sm text-muted-foreground">{service.therapy_type}</p>
                    </div>
                    <div className="text-right flex items-start gap-2">
                      <div>
                        <p className="text-lg font-medium text-primary">₹{service.total_amount}</p>
                        <p className="text-xs text-muted-foreground">Base: ₹{service.base_price}</p>
                      </div>
                      {isEditable(service) && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-8 w-8 p-0"
                          onClick={() => handleEditClick(service)}
                          title="Edit service"
                        >
                          <Pencil className="w-4 h-4 text-amber-600" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50">
                    <p className="text-xs text-muted-foreground">{service.date} at {service.time}</p>
                    <p className="text-xs text-muted-foreground">{service.therapy_duration}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Edit Service Dialog */}
      <Dialog open={!!editingService} onOpenChange={() => setEditingService(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Edit Service Entry</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Customer Name</Label>
              <Input 
                value={editForm.customer_name}
                onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Customer Phone</Label>
              <Input 
                value={editForm.customer_phone}
                onChange={(e) => setEditForm({ ...editForm, customer_phone: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Base Price (₹)</Label>
              <Input 
                type="number"
                value={editForm.base_price}
                onChange={(e) => setEditForm({ ...editForm, base_price: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Discount Amount (₹)</Label>
              <Input 
                type="number"
                value={editForm.discount_amount}
                onChange={(e) => setEditForm({ ...editForm, discount_amount: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Discount Reason</Label>
              <Input 
                value={editForm.discount_reason}
                onChange={(e) => setEditForm({ ...editForm, discount_reason: e.target.value })}
                placeholder="Optional"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Input 
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Optional notes"
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingService(null)}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              <Save className="w-4 h-4 mr-1" /> Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TherapistPerformance;